import { useCallback, useEffect, useMemo, useState } from "react";
import api, { API, formatTL } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { PageHeader, KpiCard, Panel, BarList, ChannelBadge, EmptyState } from "../../components/common";
import { CHANNELS, channelColor, channelLabel, fmtDuration, timeAgo } from "../../lib/channels";
import {
  Users, Radio, Eye, PhoneCall, MessageCircle, Percent, Clock, Download, RefreshCw, MousePointerClick,
  Smartphone, Monitor, Tablet, FileText,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const DeviceIcon = ({ d }) => (d === "mobil" ? <Smartphone className="h-3.5 w-3.5" /> : d === "tablet" ? <Tablet className="h-3.5 w-3.5" /> : <Monitor className="h-3.5 w-3.5" />);

export default function Visitors() {
  const [days, setDays] = useState("7");
  const [data, setData] = useState(null);
  const [live, setLive] = useState({ active: 0, sessions: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [o, l] = await Promise.all([api.get("/analytics/overview", { params: { days } }), api.get("/analytics/live")]);
    setData(o.data);
    setLive(l.data);
    setLoading(false);
  }, [days]);

  useEffect(() => { setLoading(true); load().catch(() => setLoading(false)); }, [load]);
  // Canlı veri: 15 sn'de bir yenile
  useEffect(() => {
    const t = setInterval(() => { api.get("/analytics/live").then((r) => setLive(r.data)).catch(() => {}); }, 15000);
    return () => clearInterval(t);
  }, []);

  const usedChannels = useMemo(() => {
    if (!data) return [];
    const set = new Set();
    data.daily.forEach((d) => Object.keys(d).forEach((k) => { if (CHANNELS[k] && d[k]) set.add(k); }));
    return [...set];
  }, [data]);

  const download = async () => {
    const res = await api.get("/analytics/export", { params: { days }, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "ziyaretciler.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading || !data) return <div className="text-slate-500" data-testid="visitors-loading">Yükleniyor...</div>;
  const k = data.kpi;
  const deviceData = data.devices;

  return (
    <div className="space-y-6" data-testid="visitors-page">
      <PageHeader
        eyebrow="Web Analitik"
        title="Ziyaretçiler & Kanallar"
        subtitle="Siteye kim, nereden ve hangi reklamla geldi; kaç kişi aradı, form doldurdu veya servis kaydına dönüştü."
      >
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-40 bg-white" data-testid="range-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Bugün</SelectItem>
            <SelectItem value="7">Son 7 gün</SelectItem>
            <SelectItem value="30">Son 30 gün</SelectItem>
            <SelectItem value="90">Son 90 gün</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} data-testid="refresh-visitors"><RefreshCw className="h-4 w-4 mr-2" />Yenile</Button>
        <Button variant="outline" onClick={download} data-testid="export-visitors"><Download className="h-4 w-4 mr-2" />CSV</Button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Radio} label="Şu An Aktif" value={live.active} hint="son 5 dakika" tone="emerald" testid="kpi-active" />
        <KpiCard icon={Users} label="Bugün (tekil)" value={k.today} hint={`dün ${k.yesterday} • 7 gün ${k.week} • 30 gün ${k.month}`} tone="blue" testid="kpi-today" />
        <KpiCard icon={Eye} label="Bugün Sayfa Görüntüleme" value={k.pageviews_today} tone="violet" />
        <KpiCard icon={Percent} label="Dönüşüm Oranı" value={`%${k.conversion_rate}`} hint="arama / WhatsApp / form" tone="amber" testid="kpi-conv" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={PhoneCall} label="Telefon Tıklaması" value={k.calls} hint={`seçili dönem`} tone="rose" />
        <KpiCard icon={MessageCircle} label="WhatsApp Tıklaması" value={k.whatsapp} tone="emerald" />
        <KpiCard icon={MousePointerClick} label="Form Gönderimi" value={k.forms} hint="iletişim + bayilik" tone="cyan" />
        <KpiCard icon={Clock} label="Ort. Süre / Çıkış" value={fmtDuration(k.avg_duration)} hint={`hemen çıkma %${k.bounce_rate}`} tone="slate" />
      </div>

      <Tabs defaultValue="overview" urlTab>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">Genel Bakış</TabsTrigger>
          <TabsTrigger value="live" data-testid="tab-live">Canlı ({live.active})</TabsTrigger>
          <TabsTrigger value="channels" data-testid="tab-channels">Kanal Performansı</TabsTrigger>
          <TabsTrigger value="content">Sayfalar & Kaynaklar</TabsTrigger>
          <TabsTrigger value="sessions">Son Oturumlar</TabsTrigger>
        </TabsList>

        {/* ---------------- GENEL BAKIŞ ---------------- */}
        <TabsContent value="overview" className="space-y-6">
          <Panel title="Günlük Ziyaretçi" subtitle="Tekil oturum sayısı • kanala göre dağılım">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="label" stroke="#94A3B8" fontSize={12} />
                  <YAxis stroke="#94A3B8" fontSize={12} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
                  <Legend formatter={(v) => channelLabel(v)} wrapperStyle={{ fontSize: 12 }} />
                  {usedChannels.map((ch) => (
                    <Bar key={ch} dataKey={ch} name={ch} stackId="a" fill={channelColor(ch)} isAnimationActive={false} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
            {usedChannels.length === 0 && <div className="text-center text-sm text-slate-400 -mt-48 mb-40">Henüz ziyaretçi verisi yok. İzleme betiği siteye eklendiğinde burada görünür.</div>}
          </Panel>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Panel title="Saatlik Yoğunluk" subtitle="Hangi saatlerde ziyaret alıyorsunuz (TR saati)" className="lg:col-span-2">
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.hourly}>
                    <XAxis dataKey="hour" stroke="#94A3B8" fontSize={11} interval={1} />
                    <YAxis stroke="#94A3B8" fontSize={11} allowDecimals={false} width={30} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
                    <Bar dataKey="value" name="Ziyaret" fill="#F97316" radius={[3, 3, 0, 0]} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
            <Panel title="Cihaz">
              <BarList data={deviceData} color="#2563EB" />
              <div className="mt-5 pt-4 border-t border-slate-100"><div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Tarayıcı</div><BarList data={data.browsers.slice(0, 4)} color="#8B5CF6" /></div>
            </Panel>
          </div>
        </TabsContent>

        {/* ---------------- CANLI ---------------- */}
        <TabsContent value="live">
          <Panel title="Şu An Sitede Olanlar" subtitle="Son 5 dakikada hareket eden ziyaretçiler • 15 sn'de bir otomatik yenilenir"
            right={<span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700"><span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />CANLI</span>}>
            {live.sessions.length === 0 ? (
              <EmptyState icon={Radio} title="Şu an aktif ziyaretçi yok" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="live-table">
                  <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <tr><th className="py-2 pr-3">IP</th><th className="pr-3">Kanal</th><th className="pr-3">Şu an baktığı sayfa</th><th className="pr-3">Cihaz</th><th className="pr-3">Süre</th><th className="pr-3">Sayfa</th><th>Son hareket</th></tr>
                  </thead>
                  <tbody>
                    {live.sessions.map((s) => (
                      <tr key={s.id} className="border-b border-slate-100">
                        <td className="py-2.5 pr-3 font-mono text-xs">{s.ip}</td>
                        <td className="pr-3"><ChannelBadge channel={s.channel} /></td>
                        <td className="pr-3 max-w-[260px] truncate text-slate-700" title={s.current_page}>{s.current_page}</td>
                        <td className="pr-3"><span className="inline-flex items-center gap-1 text-slate-600"><DeviceIcon d={s.device} />{s.browser}</span></td>
                        <td className="pr-3 font-mono text-xs">{fmtDuration(s.duration_sec)}</td>
                        <td className="pr-3 font-mono text-xs">{s.pages_count}</td>
                        <td className="text-slate-500 text-xs">{timeAgo(s.last_seen)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </TabsContent>

        {/* ---------------- KANAL PERFORMANSI ---------------- */}
        <TabsContent value="channels">
          <Panel title="Kanal Performans Tablosu" subtitle="Reklam mı, organik mi? Hangi kanal ziyaretçiyi aramaya ve gerçek ciroya dönüştürüyor?">
            {data.channels.length === 0 ? <EmptyState icon={FileText} title="Henüz kanal verisi yok" /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="channel-table">
                  <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="py-2 pr-3">Kanal</th><th className="pr-3 text-right">Ziyaretçi</th><th className="pr-3 text-right">Sayfa</th>
                      <th className="pr-3 text-right">Arama</th><th className="pr-3 text-right">WhatsApp</th><th className="pr-3 text-right">Form</th>
                      <th className="pr-3 text-right">Dönüşüm</th><th className="pr-3 text-right">Hemen Çıkma</th>
                      <th className="pr-3 text-right">Servis Kaydı</th><th className="pr-3 text-right">Tamamlanan</th><th className="text-right">Ciro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.channels.map((c) => (
                      <tr key={c.channel} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`channel-row-${c.channel}`}>
                        <td className="py-2.5 pr-3"><ChannelBadge channel={c.channel} /></td>
                        <td className="pr-3 text-right font-mono">{c.sessions}</td>
                        <td className="pr-3 text-right font-mono">{c.pageviews}</td>
                        <td className="pr-3 text-right font-mono">{c.calls}</td>
                        <td className="pr-3 text-right font-mono">{c.whatsapp}</td>
                        <td className="pr-3 text-right font-mono">{c.forms}</td>
                        <td className="pr-3 text-right font-mono font-semibold text-amber-700">%{c.conv_rate}</td>
                        <td className="pr-3 text-right font-mono text-slate-500">%{c.bounce_rate}</td>
                        <td className="pr-3 text-right font-mono">{c.jobs}</td>
                        <td className="pr-3 text-right font-mono">{c.completed_jobs}</td>
                        <td className="text-right font-mono font-bold text-emerald-700">{formatTL(c.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-xs text-slate-400 mt-4">
              “Servis Kaydı / Ciro” sütunları, panelde servis kaydı açılırken seçilen (veya web mesajından otomatik gelen) kanala göre hesaplanır.
              Telefonla gelen müşterileri kaydederken “Kanal” alanını doldurursanız reklamın gerçek getirisini görebilirsiniz.
            </p>
          </Panel>
        </TabsContent>

        {/* ---------------- SAYFALAR & KAYNAKLAR ---------------- */}
        <TabsContent value="content">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Panel title="En Çok Görüntülenen Sayfalar"><BarList data={data.top_pages} color="#2563EB" /></Panel>
            <Panel title="Giriş (Landing) Sayfaları" subtitle="Ziyaretçinin siteye ilk girdiği sayfa"><BarList data={data.top_landing} color="#10B981" /></Panel>
            <Panel title="Kaynaklar"><BarList data={data.sources} color="#F97316" /></Panel>
            <Panel title="Reklam Kampanyaları" subtitle="utm_campaign / Google Ads kampanya kimliği"><BarList data={data.campaigns} color="#8B5CF6" empty="Kampanya verisi yok (reklam URL'lerine utm_campaign ekleyin)" /></Panel>
            <Panel title="Anahtar Kelimeler" subtitle="utm_term (reklam URL'sinde {keyword} kullanılırsa dolar)"><BarList data={data.terms} color="#EC4899" empty="Anahtar kelime verisi yok" /></Panel>
            <Panel title="İşletim Sistemi"><BarList data={data.os} color="#06B6D4" /></Panel>
          </div>
        </TabsContent>

        {/* ---------------- OTURUMLAR ---------------- */}
        <TabsContent value="sessions">
          <Panel title="Son Ziyaretçi Oturumları">
            {data.recent.length === 0 ? <EmptyState icon={Users} title="Henüz oturum yok" /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="sessions-table">
                  <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <tr><th className="py-2 pr-3">Zaman</th><th className="pr-3">IP</th><th className="pr-3">Kanal</th><th className="pr-3">Kaynak / Kampanya</th><th className="pr-3">Giriş sayfası</th><th className="pr-3">Sayfa</th><th className="pr-3">Süre</th><th className="pr-3">Cihaz</th><th>Aksiyon</th></tr>
                  </thead>
                  <tbody>
                    {data.recent.map((s) => (
                      <tr key={s.id} className="border-b border-slate-100 align-top">
                        <td className="py-2.5 pr-3 text-xs text-slate-500 whitespace-nowrap">{timeAgo(s.last_seen)}</td>
                        <td className="pr-3 font-mono text-xs">{s.ip}</td>
                        <td className="pr-3"><ChannelBadge channel={s.channel} /></td>
                        <td className="pr-3 text-xs text-slate-600 max-w-[170px] truncate" title={`${s.source_name} ${s.campaign || ""}`}>{s.source_name}{s.campaign ? ` / ${s.campaign}` : ""}</td>
                        <td className="pr-3 max-w-[200px] truncate" title={s.landing_page}>{s.landing_page}</td>
                        <td className="pr-3 font-mono text-xs" title={(s.pages || []).join("\n")}>{s.pages_count}</td>
                        <td className="pr-3 font-mono text-xs">{fmtDuration(s.duration_sec)}</td>
                        <td className="pr-3 text-xs text-slate-600"><span className="inline-flex items-center gap-1"><DeviceIcon d={s.device} />{s.device}</span></td>
                        <td className="text-xs">
                          {s.conv_call > 0 && <span className="inline-flex items-center gap-1 text-rose-700 font-semibold mr-2"><PhoneCall className="h-3 w-3" />Aradı</span>}
                          {s.conv_whatsapp > 0 && <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold mr-2"><MessageCircle className="h-3 w-3" />WhatsApp</span>}
                          {(s.conv_form > 0 || s.conv_dealer > 0) && <span className="text-cyan-700 font-semibold">Form</span>}
                          {!s.converted && <span className="text-slate-300">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </TabsContent>
      </Tabs>
      <div className="text-[11px] text-slate-400">API: {API}. Botlar (Googlebot, PageSpeed, AWS tarayıcıları vb.) otomatik elenir; IP’lerin son bölümü KVKK gereği maskelenir.</div>
    </div>
  );
}
